// The `kb_page` wire shape — one KB wiki page as `GET /v1/kb/:repoId/pages`,
// `GET /v1/kb/:repoId/pages/:slug` and `PUT` on that same path serve it.
//
// The entity is already half-projected by the time the route sees it:
// `RepoMemoryPage`'s timestamps arrive as ISO strings, not `Date`s, so this
// resource renames rather than converts. That is worth stating, because every
// other resource in this directory converts, and a reader who assumes it here
// would "fix" a `.toISOString()` onto a string.
//
// `text` rides whole. The page IS its text — a KB read that returned a preview
// would make the door useless to the agent it exists for — so the bound is the
// write side's (`KB_PAGE_WRITE_FIELDS`), never a truncation here.

import { z } from 'zod';
import { defineResource } from './resource';

/**
 * `GET /v1/kb/:repoId/search`'s `limit` CEILING, shared by the route's query
 * schema (`apps/api/src/routes/kb.ts`, which refuses past it rather than
 * clamping), the MCP `kb search` action (`packages/mcp/src/army/kb.ts`, which
 * clamps to it so callers of its old 1..200 range keep working) and the
 * dashboard's search (`apps/dashboard/src/lib/kb.ts`), which asks for exactly
 * this many — so all three cite ONE number instead of literal `25`s drifting apart.
 */
export const KB_SEARCH_LIMIT_MAX = 25;

/**
 * The camelCase shape the projection reads — structural, so the resource states
 * its own minimum. `KbPageView` (`apps/api/src/routes/kb.ts`) satisfies it.
 */
export type KbPageEntity = {
  slug: string;
  text: string;
  links: string[];
  updatedByRunId: string | null;
  /** Last-write time, ISO-8601 — ALREADY a string on the entity. */
  ts: string;
  usedCount: number;
  /** Last retrieval time, ISO-8601, or null if never retrieved. */
  lastUsedAt: string | null;
};

/** The published body. Field names and nullability are the existing contract. */
export const KbPageWireSchema = z.object({
  slug: z.string(),
  text: z.string(),
  /** Outbound wiki links parsed from the page body. Empty, never null. */
  links: z.array(z.string()),
  /** The run that last wrote the page; null for a page no run has touched. */
  updated_by_run_id: z.string().nullable(),
  ts: z.string(),
  /** How many times a retrieval has served this page. `0` for a never-read page. */
  used_count: z.number(),
  last_used_at: z.string().nullable(),
});

/** What a client should type against for a KB page body. */
export type KbPageWire = z.infer<typeof KbPageWireSchema>;

export const KbPageResource = defineResource<KbPageEntity, typeof KbPageWireSchema>({
  schema: KbPageWireSchema,
  serialize: (p) => ({
    slug: p.slug,
    text: p.text,
    links: p.links,
    updated_by_run_id: p.updatedByRunId,
    ts: p.ts,
    used_count: p.usedCount,
    last_used_at: p.lastUsedAt,
  }),
});
